import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import './StylishDateTimePicker.css';
import DatePicker from 'react-date-picker';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import PratusSelect from '../New/PratusSelect';
import { timeZoneOptions } from 'constants/timezones';

dayjs.extend(customParseFormat);

const DEFAULT_TIME_24H = '00:00';

const getNormalizedDateTimeValue = (value) => {
  if (!value) {
    return null;
  }

  const parsedValue = dayjs(value);
  return parsedValue.isValid() ? parsedValue.toISOString() : null;
};

const timeOptions = (() => {
  const newTimeOptions = [];
  const format = 'HH:mm';

  for (let i = 0; i < 24; i++) {
    for (let j = 0; j < 60; j += 30) {
      const time = dayjs().hour(i).minute(j);
      newTimeOptions.push({
        label: time.format(format),
        value: time.format('HH:mm'),
      });
    }
  }
  return newTimeOptions;
})();

const Input = (props) => <components.Input {...props} isHidden={false} />;

// We expect the value to come as an iso time string
// We trigger the callback using the same format
export default function StylishDateTimePicker({
  value,
  onChange,
  type,
  minDate,
  maxDate,
  onBlur,
  disabled,
  dateLabel,
  timeLabel,
  disableDateTimeLabel,
  required,
  customTimeOptions,
  timeZone,
  timeZoneValue,
  onTimeZoneChange,
  timeZoneLabel,
  ...props
}) {
  const user = useSelector((state) => state.prepare.user);

  const [date, setDate] = useState();
  const [time, setTime] = useState();

  const selectRef = useRef();
  const [inputValue, setInputValue] = useState(DEFAULT_TIME_24H);
  const inputValueRef = useRef(DEFAULT_TIME_24H);

  const [localTimeOptions, setLocalTimeOptions] = useState([]);

  useEffect(() => {
    if (customTimeOptions && customTimeOptions.length > 0) {
      setLocalTimeOptions(customTimeOptions);
    } else if (user.timeFormat === 'hour12Mode') {
      setLocalTimeOptions(
        timeOptions.map((option) => ({
          ...option,
          label: dayjs(option.value, 'HH:mm').format('h:mm A'),
        }))
      );
    } else {
      setLocalTimeOptions(timeOptions);
    }
  }, [customTimeOptions, user.timeFormat]);

  const formatOptionLabel = ({ label }, context) => {
    if (context.context === 'menu') {
      return (
        <div>
          <span className="material-symbols-outlined icon">
            nest_clock_farsight_analog
          </span>
          <div className="label">{label}</div>
        </div>
      );
    } else {
      return <div className="label">{label}</div>;
    }
  };

  useEffect(() => {
    if (value) {
      const initialDateTime = dayjs(value);
      const time24 =
        user.timeFormat === 'hour12Mode'
          ? initialDateTime.format('h:mm A')
          : initialDateTime.format('HH:mm');
      setTime(time24);
      setInputValue(time24);
      inputValueRef.current = time24;
      setDate(initialDateTime.toDate());
    } else {
      const defaultTime = DEFAULT_TIME_24H;
      setTime(defaultTime);
      setInputValue(defaultTime);
      inputValueRef.current = defaultTime;
      setDate(value);
    }
  }, [value, user.timeFormat]);

  useEffect(() => {
    if (date && time) {
      if (typeof onBlur === 'function') {
        onBlur();
      }
      let newValue;

      const hours = parseInt(time.substring(0, time.indexOf(':')));
      const minutes = parseInt(time.substring(time.indexOf(':') + 1));
      if (user.timeFormat !== 'hour12Mode') {
        newValue = dayjs(date).hour(hours).minute(minutes).toISOString();
      } else {
        if (time.toUpperCase().includes('PM')) {
          newValue = dayjs(date)
            .hour(hours < 12 ? hours + 12 : hours)
            .minute(minutes)
            .toISOString();
        } else {
          newValue = dayjs(date)
            .hour(hours === 12 ? 0 : hours)
            .minute(minutes)
            .toISOString();
        }
      }
      if (
        getNormalizedDateTimeValue(value) !== newValue &&
        typeof onChange === 'function'
      ) {
        onChange(newValue);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, time]);

  const checkIfValid = (inputValue) => {
    return user.timeFormat === 'hour12Mode'
      ? dayjs(inputValue, 'h:mm A', true).isValid() ||
          dayjs(inputValue, 'h:mmA', true).isValid() ||
          dayjs(inputValue, 'hmmA', true).isValid() ||
          dayjs(inputValue, 'hmm A', true).isValid()
      : dayjs(inputValue, 'HH:mm', true).isValid() ||
          dayjs(inputValue, 'HHmm', true).isValid();
  };

  const getDefaultTime = () => {
    return user.timeFormat === 'hour12Mode' ? '12:00 AM' : DEFAULT_TIME_24H;
  };

  const validateAndSetTime = (value) => {
    if (!value || value.length === 0) {
      const defaultTime = getDefaultTime();
      setInputValue(defaultTime);
      setTime(defaultTime);
      inputValueRef.current = defaultTime;
      return;
    }

    if (checkIfValid(value)) {
      const formattedTime = formatTime(value, user.timeFormat);
      setTime(formattedTime);
      setInputValue(formattedTime);
      inputValueRef.current = formattedTime;
    } else {
      // Invalid, revert to previous time
      setInputValue(time);
      inputValueRef.current = time;
    }
  };

  return (
    <>
      <div className="form-datetimepicker" id="form-datetimepicker">
        <div className="d-flex flex-wrap gap-2 align-items-end">
          <div
            style={{
              flex: type === 'datetime-local' ? '2 1 0' : '1 1 100%',
              minWidth: '140px',
            }}
          >
            {!disableDateTimeLabel && (
              <label className="form-label">
                {dateLabel ? `${dateLabel}` : 'Date:'}
                {required ? (
                  <span aria-label="Required field" className="required">
                    *
                  </span>
                ) : (
                  ''
                )}
              </label>
            )}

            <DatePicker
              onChange={setDate}
              value={date}
              clearIcon={null}
              calendarIcon={null}
              minDate={minDate ? new Date(minDate) : undefined}
              maxDate={maxDate ? new Date(maxDate) : undefined}
              disabled={disabled}
              dayPlaceholder="DD"
              monthPlaceholder="MM"
              yearPlaceholder="YYYY"
              calendarType={'US'}
              formatShortWeekday={(locale, date) => {
                const weekday = date.toLocaleDateString(locale, {
                  weekday: 'short',
                });
                if (weekday === 'Sun' || weekday === 'Thu') {
                  return weekday.slice(0, 2);
                }
                return weekday.slice(0, 1);
              }}
              {...props}
            />
          </div>
          {type === 'datetime-local' && (
            <>
              <div
                style={{
                  flex: timeZone ? '1 1 0' : '2 1 0',
                  minWidth: '120px',
                }}
              >
                {!disableDateTimeLabel && (
                  <label className="form-label">
                    {timeLabel ? `${timeLabel}` : 'Time:'}
                    {required ? (
                      <span aria-label="Required field" className="required">
                        *
                      </span>
                    ) : (
                      ''
                    )}
                  </label>
                )}
                <div className={'form-selectbox time-select'}>
                  <CreatableSelect
                    isDisabled={disabled}
                    ref={selectRef}
                    onInputChange={(newInputValue, { action }) => {
                      if (action === 'input-change') {
                        setInputValue(newInputValue);
                        inputValueRef.current = newInputValue;
                      }
                    }}
                    inputValue={inputValue}
                    components={{
                      Input,
                    }}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    options={localTimeOptions}
                    openMenuOnClick={false}
                    onBlur={() => validateAndSetTime(inputValueRef.current)}
                    value={
                      inputValue?.length < 1
                        ? null
                        : {
                            value: time,
                            label: formatTime(time, user.timeFormat),
                          }
                    }
                    onKeyDown={(e) => {
                      // Limit input to valid characters
                      const allowedKeys = [
                        'Backspace',
                        'ArrowLeft',
                        'ArrowRight',
                      ];
                      const isValidKey =
                        (user.timeFormat === 'hour12Mode'
                          ? /^[\d:AMPamp\s]+$/.test(e.key)
                          : /^[\d:]+$/.test(e.key)) ||
                        allowedKeys.includes(e.key);

                      if (!isValidKey) {
                        e.preventDefault();
                      }

                      if (e.key === 'Enter') {
                        validateAndSetTime(e.target.value);
                        selectRef.current?.blur();
                      }
                    }}
                    onChange={(selected) => {
                      if (selected) {
                        const formattedTime = formatTime(
                          selected.value,
                          user.timeFormat,
                          'HH:mm'
                        );

                        inputValueRef.current = formattedTime;
                        setInputValue(formattedTime);
                        setTime(formattedTime);
                      }
                    }}
                    placeholder={''}
                    filterOption={false}
                    menuPlacement="auto"
                    noOptionsMessage={() => 'No Items Found'}
                    formatOptionLabel={formatOptionLabel}
                    isValidNewOption={(inputValue) => {
                      return checkIfValid(inputValue);
                    }}
                  />
                </div>
              </div>
              {timeZone && (
                <div style={{ flex: '1 1 0', minWidth: '100px' }}>
                  {timeZoneLabel && (
                    <label htmlFor="timeZone" className="mb-2">
                      Time Zone:
                    </label>
                  )}
                  <PratusSelect
                    value={timeZoneOptions.find(
                      (option) => option.value === timeZoneValue
                    )}
                    options={timeZoneOptions}
                    onChange={(e) => onTimeZoneChange(e)}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}

const formatTime = (timeValue, timeFormat, inputFormat = 'HH:mm') => {
  if (timeFormat === 'hour12Mode') {
    return dayjs(timeValue, inputFormat).format('h:mm A');
  }
  return dayjs(timeValue, inputFormat).format('HH:mm');
};
